<template>
  <div class="">
    <wizard-page-header :stepTitle="stepTitle" :readonly="readonly"></wizard-page-header>

    <div :class="[readonly && 'opacity-70 pointer-events-none']" class="content-container">
      <a-col :lg="6" :xs="0">
        <new-order-sidebar :canAdd="true" :order_items="order.order_items" :readonly="readonly" />
      </a-col>
      <a-col :lg="18" :xs="24">
        <!-- Loading -->
        <div
          class="flex flex-col items-center w-full"
          v-if="
            isGeneratingOffers || (contactInfoNeeded && order.offers_status == 'success_no_offers')
          "
        >
          <a-spin :spinning="true" size="large" class="mb-8"></a-spin>
          <h2 class="italic font-bold text-center text-22px">
            Stiamo cercando l’offerta perfetta per i tuoi strumenti...
          </h2>
        </div>

        <!-- no offers -->
        <div v-else-if="order.offers_status == 'success_no_offers'">
          <h2 class="mb-8 font-bold text-center text-22px">
            Non abbiamo trovato l'offerta perfetta per te.
          </h2>
          <p class="mb-10 text-lg text-center">
            La nostra ricerca non si ferma.
            <br />
            I nostri broker si metteranno presto all'opera per trovare l'offerta su misura alle tue
            esigenze.
          </p>

          <div
            class="flex flex-col items-center px-6 py-4 mx-4 mt-6 mb-3 text-lg border-2 border-green-400 rounded md:mx-auto bg-green-50 justify-evenly md:w-max"
          >
            <h2
              class="w-full mb-8 font-bold text-center text-green-500 md:text-left text-22px"
              v-if="!noOfferRequestSent"
            >
              Vuoi essere contattato dai nostri broker con un offerta personalizzata?
            </h2>
            <h2 class="mb-8 font-bold text-center text-green-500 text-22px" v-else>
              <b>Richiesta inviata correttamente!</b>
              <br />
              Sarai ricontattato da un nostro operatore al più presto
            </h2>

            <div
              class="flex flex-col-reverse items-center justify-end w-full mt-4 lg:flex-row"
              v-if="!noOfferRequestSent"
            >
              <div
                class="mt-6 font-semibold text-green-500 cursor-pointer md:mr-6 md:mt-0"
                @click="resetOrder"
              >
                No grazie
              </div>

              <a-button
                type="primary"
                @click="sendNoOfferContactRequest"
                :loading="sendingNoOfferRequest"
              >
                Sì, contattami con ulteriori offerte
              </a-button>
            </div>

            <div class="flex items-center justify-end w-full mt-4" v-else>
              <div class="mr-6 font-semibold text-green-500 cursor-pointer" @click="resetOrder">
                Torna alla pagina iniziale
              </div>
            </div>
          </div>
        </div>

        <!-- Sneak peek -->
        <div
          class="relative flex flex-col items-center w-full px-3 sm:px-0"
          :class="
            !isGeneratingOffers &&
              offersToShow.length > 1 &&
              'md:grid justify-items-center grid-cols-2 gap-4'
          "
          v-else-if="$store.state.order"
        >
          <h2 class="col-span-2 mb-10 font-bold text-center text-22px">
            {{
              offersToShow.length === 1
                ? "Abbiamo trovato l'offerta perfetta"
                : `Abbiamo trovato ${offersToShow.length} offerte perfette per le tue esigenze`
            }}
          </h2>

          <offer
            v-for="o in offersToShow"
            :key="o.id"
            :blurred="contactInfoNeeded"
            :tailored="offersToShow.length === 1"
            :offer="o"
            :loading="selectingOffer === o.id"
            :disabled="selectingOffer && selectingOffer !== o.id"
            @pick="() => selectOffer(o.id)"
          />

          <canvas
            ref="confettiCanvas"
            class="absolute top-0 left-0 w-full pointer-events-none"
            :style="{ height: contactInfoNeeded ? '200px' : '100%' }"
          ></canvas>
        </div>

        <offer-feedback
          @reset="resetOrder"
          v-if="
            !readonly &&
              !contactInfoNeeded &&
              !order.feedback &&
              !isGeneratingOffers &&
              order.offers_status != 'success_no_offers'
          "
          class="col-span-2 mx-3 mt-20 sm:mx-auto"
        ></offer-feedback>

        <!-- Spacer to keep the page full height -->
        <div v-if="contactInfoNeeded" :style="{ height: contactFormSpacerSize + 'px' }"></div>

        <!-- Contact data form -->
        <a-card
          class="absolute"
          :style="{
            top: '180px',
            width: '100%',
            maxWidth: '700px',
            left: '50%',
            transform: 'translateX(-50%)'
          }"
          ref="contactNeededCard"
          v-if="contactInfoNeeded"
        >
          <h2 class="mb-2 text-lg font-bold text-center md:mb-10 md:block">
            Inserisci i tuoi dati per visualizzare le offerte
          </h2>

          <contact-info-form />
        </a-card>
      </a-col>
    </div>
  </div>
</template>

<script>
import api from '@/api.js';
import confetti from 'canvas-confetti';

export default {
  name: 'show-offers',

  data() {
    return {
      currentStep: 2,
      stepTitle: 'Le offerte che abbiamo trovato',
      submitting: false,
      isGeneratingOffers: false,
      sendingNoOfferRequest: false,
      noOfferRequestSent: false,
      selectingOffer: null,
      showCelebration: true,
      activeTimeout: null,
      contactFormSpacerSize: 0
    };
  },

  computed: {
    readonly() {
      return !!this.order?.chosen_offer;
    },

    offersToShow() {
      if (!this.order) return [];

      return this.order.valid_offers;
    },

    order() {
      return this.$store.state.order;
    },

    mobile() {
      return this.$mq.resize && this.$mq.below('768px');
    },

    contactInfoNeeded() {
      const val = !this.order?.customer || !this.order?.customer?.email;

      if (val) {
        this.$nextTick(() => {
          // get the rendered size of the contact form (that is absolute positioned) and use it
          // to create a spacer to give the page the right dimension
          // without hiding the footer
          const size = this.$refs.contactNeededCard.$el.getBoundingClientRect();
          this.contactFormSpacerSize = size.height - 180;
        });
      }

      return val;
    }
  },

  watch: {
    '$store.state.order': function(order, old) {
      this.isGeneratingOffers = order.offers_status === 'generating';

      if (order.offers_status === 'success' && order.offers.length > 0) {
        this.isGeneratingOffers = false;
        if (this.showCelebration) {
          this.$nextTick(this.celebrate);
        }
      }

      if (this.isGeneratingOffers) {
        this.activeTimeout = setTimeout(() => {
          this.getOrderStatus();
        }, 3000);
      }

      //
      const contactInfo = order.customer || order.customer?.email;
      const contactInfoBefore = old.customer || old.customer?.email;
      if (contactInfo && !contactInfoBefore) {
        window.scrollTo(0, 0);
      }
    }
  },

  async mounted() {
    // show fake loader
    if (this.$route.query.l == 1) {
      this.isGeneratingOffers = true;
      this.activeTimeout = setTimeout(() => {
        this.$router.replace({ name: 'offers', query: { l: null } });
        this.getOrderStatus();
      }, 4000);
    } else {
      // show real status of the order
      this.getOrderStatus();
      this.showCelebration = false;
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.activeTimeout) window.clearTimeout(this.activeTimeout);

    // called when the route that renders this component is about to be navigated away from.
    // As with `beforeRouteUpdate`, it has access to `this` component instance.
    return next();
  },

  methods: {
    celebrate() {
      const canvas = this.$refs.confettiCanvas;

      // if for some reasone canvas is not there, avoid confetti to now break the flow
      if (!canvas) return;

      canvas.confetti = canvas.confetti || confetti.create(canvas, { resize: true });

      canvas.confetti({
        zIndex: 99999,
        particleCount: 200,
        spread: 60,
        origin: { y: 1 },
        colors: ['#ed8936', '#EC8023']
      });
    },

    getOrderStatus() {
      this.$store.dispatch('getOrder', true);
    },

    async sendNoOfferContactRequest() {
      this.sendingNoOfferRequest = true;
      try {
        await api.sendNoOfferContactRequest();
        this.sendingNoOfferRequest = false;
        this.noOfferRequestSent = true;
      } catch (error) {
        console.log('Error sending request', error);
        this.sendingNoOfferRequest = false;
        this.noOfferRequestSent = true;
      }
    },

    async resetOrder() {
      await api.resetOrder();
      this.$router.push('/');
      this.$store.commit('resetOrder');
    },

    async selectOffer(id) {
      this.selectingOffer = id;

      try {
        await api.chooseOffer({ offer_id: id });
        await this.$store.dispatch('getOrder', true);
      } catch (err) {
        console.log('Error chosing offer', err);
        this.selecting = false;
        return;
      }

      this.selecting = null;

      this.$router.push({ name: 'customer-full-info' });
    }
  }
};
</script>

<style lang="scss"></style>
